import { useState, useEffect, useMemo, useCallback, useRef } from "react";

import * as Material from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import HelpIcon from "@mui/icons-material/Help";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import FilterListIcon from "@mui/icons-material/FilterList";
import AppShortcutIcon from "@mui/icons-material/AppShortcut";
import { DataGrid, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import LandscapeIcon from "@mui/icons-material/Landscape";
import HubIcon from "@mui/icons-material/Hub";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import MainButton from "../../SmallComponents/MainButton";
import SelectChallenge from "../../SelectChallenge";

import CustomModal from "../../CustomModal";

import { createTheme, ThemeProvider } from "@mui/material/styles";

import api from "../../../helpers/api.js";

import Config, { token } from "../../../config.js";

import LinearProgress from "@mui/material/LinearProgress";

import Screen from "../../Screen";
import Pagination from "../../Pagination";

import { CreateModal } from "../../../pages/Groups";

import "./styles.css";

import { useChallenge } from "../../../contexts/ChallengeContext";

export default function CreateNewUserModal({
  users,
  open,
  setOpen,
  handleCreated = () => {},
  creationPreParams,
}) {
  const { currentChallenge, groups, loading, handleOpenChallengesSelect } =
    useChallenge();

  const [creating, setCreating] = useState(false);

  const [name, setName] = useState(creationPreParams?.name || "");
  const [email, setEmail] = useState(creationPreParams?.email || "");
  const [phone, setPhone] = useState(creationPreParams?.phone || "+55 (");
  const [group, setGroup] = useState("-");

  const [fixImportRecordId, setFixImportRecordId] = useState(
    creationPreParams?.fixImportRecordId || null
  );

  const nameRef = useRef();
  const emailRef = useRef();
  const phoneRef = useRef();

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  useEffect(() => {
    if (nameError && isNameValid()) setNameError(false);
  }, [name]);
  useEffect(() => {
    if (emailError && isEmailValid()) setEmailError(false);
  }, [email]);
  useEffect(() => {
    if (phoneError && isPhoneValid()) setPhoneError(false);
  }, [phone]);

  useEffect(() => {
    if (creationPreParams?.phone) {
      const formattedValue = formatPhoneNumber(creationPreParams.phone);
      setPhone(formattedValue);
    }
  }, []);

  async function handleCreateUser() {
    const valid = handleValidate();

    if (!valid) {
      window.setSnackbar("Reveja os dados do participante para continuar");
      return;
    }

    const SubmitParse = {
      formatName() {
        return name
          .trim()
          .split(" ")
          .map((word) => {
            if (word.length <= 2) {
              return word.toLowerCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          })
          .join(" ");
      },
      formatEmail() {
        return email.trim().toLowerCase();
      },
      formatPhone() {
        return `+${phone.replace(/[^\d]/g, "")}`;
      },
    };

    setCreating(true);

    try {
      const form = {
        challenge_id: currentChallenge?.challenge_id,
        src: "manual_add",
        name: SubmitParse.formatName(),
        email: SubmitParse.formatEmail(),
        phone: SubmitParse.formatPhone(),
      };

      if (group && group !== "-") form.group_id = group;

      if (fixImportRecordId) form.fixImportRecordId = fixImportRecordId;

      const result = await api.post("/challenge/members/create", form);

      if (result.data.success) {
        window.setSnackbar("Participante criado com sucesso");
        handleCreated();
      } else {
        window.setSnackbar(
          "Houve um erro criar este participante. Por favor tente novamente mais tarde"
        );
      }
    } catch (e) {
      console.log(e);
      window.setSnackbar(
        "Houve um erro ao criar este participante, reveja os dados inseridos e tente novamente"
      );
    }

    setCreating(false);
  }

  function handleValidate() {
    const nameValid = isNameValid();

    if (!nameValid) {
      nameRef?.current.focus();
      setNameError(true);
      return false;
    }

    const emailValid = isEmailValid();

    if (!emailValid) {
      emailRef?.current.focus();
      setEmailError(true);
      return false;
    }

    const phoneValid = isPhoneValid();

    if (!phoneValid) {
      phoneRef?.current.focus();
      setPhoneError(true);
      return false;
    }

    return true;
  }

  function isNameValid() {
    const nameParts = name.trim().split(" ");
    return nameParts.length >= 2 && nameParts.every((part) => part.length > 1);
  }
  function isEmailValid() {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  }
  function isPhoneValid() {
    const phonePattern = /^\+55 \(\d{2}\) \d \d{4}-\d{4}$/;
    return phonePattern.test(phone);
  }

  function handleNameInput(v) {
    setName(v.target.value);
  }
  function handleEmailInput(v) {
    const value = v.target.value;
    setEmail(value);
  }
  function handlePhoneInput(event) {
    const { value } = event.target;
    const formattedValue = formatPhoneNumber(value);
    setPhone(formattedValue);
  }

  return (
    <>
      <CustomModal
        maxWidth={450}
        bottomButtonCancelText="Cancelar"
        hideSecondaryButton={false}
        loading={creating}
        title="Adicionar novo usuário"
        handleSubmit={handleCreateUser}
        bottomButtonSumbitText="Criar usuário"
        open={open}
        handleClose={() => setOpen(false)}
        width="100%"
        bodyWrapperStyle={{}}
        modalClass="select-challenge-modal"
        bottomButtonsOrientation="vertical"
        bodyComponent={
          <div className="container">
            <h4 className="mb-2">Dados do usuário</h4>

            <Material.TextField
              inputRef={nameRef}
              className="mb-3"
              autoFocus
              label="Nome completo"
              value={name}
              error={nameError}
              onInput={handleNameInput}
              variant="outlined"
              helperText={nameError && "Insira um nome completo"}
            />
            <Material.TextField
              inputRef={emailRef}
              input
              className="mb-3"
              label="E-mail"
              value={email}
              error={emailError}
              onInput={handleEmailInput}
              variant="outlined"
              helperText={emailError && "Insira um email válido"}
            />

            <Material.TextField
              inputRef={phoneRef}
              InputLabelProps={{ shrink: true }}
              label="Celular com DDD"
              value={phone}
              error={phoneError}
              onInput={handlePhoneInput}
              variant="outlined"
              helperText={phoneError && "Insira um número de celular válido"}
            />
            <Material.Alert severity="info" className="mt-2">
              <b>Atenção!</b> Adicione um celular válido, pois ele será usado
              pelo usuário ao entrar no app
            </Material.Alert>

            <div className="mb-2 mt-2">
              <h5 className="mb-1">Adicionar usuário à comunidade:</h5>
              <SelectChallenge />
            </div>
          </div>
        }
      />
    </>
  );
}

const formatPhoneNumber = (value) => {
  // Remove todos os caracteres não numéricos, exceto o prefixo +55
  const phoneNumber = value.replace(/[^\d]/g, "").slice(2);

  // Adiciona o código de país +55 e formata conforme a máscara desejada
  const match = phoneNumber.match(/^(\d{2})(\d{1})(\d{4})(\d{4})$/);

  if (match) {
    return `+55 (${match[1]}) ${match[2]} ${match[3]}-${match[4]}`;
  } else if (phoneNumber.length <= 2) {
    return `+55 (${phoneNumber}`;
  } else if (phoneNumber.length <= 3) {
    return `+55 (${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(2)}`;
  } else if (phoneNumber.length <= 7) {
    return `+55 (${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(
      2,
      3
    )} ${phoneNumber.slice(3)}`;
  } else {
    return `+55 (${phoneNumber.slice(0, 2)}) ${phoneNumber.slice(
      2,
      3
    )} ${phoneNumber.slice(3, 7)}-${phoneNumber.slice(7, 11)}`;
  }
};
